import React, { useState, useEffect } from 'react';

export default function Offerte() {
  // Calculator state
  const [calcUren, setCalcUren] = useState(8);
  const [calcDuur, setCalcDuur] = useState('3');
  
  // Calculated values
  const UURTARIEF = 190;
  const weeksInPeriod = {
    '3': 13,
    '6': 26,
    '12': 52
  };
  const duurLabels = {
    '3': '3 maanden (1 kwartaal)',
    '6': '6 maanden (2 kwartalen)',
    '12': '12 maanden (1 jaar)'
  };
  
  const totaalUren = calcUren * weeksInPeriod[calcDuur];
  const totaalBedrag = totaalUren * UURTARIEF;
  
  // Form state
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    organisatie: '',
    telefoon: '',
    // Inzet velden worden automatisch ingevuld vanuit calculator
    diensten: [],
    beschikbareDagen: [],
    toelichting: '',
    privacy: false
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dienstenOpties = [
    'Psychiatrische consultatie',
    'Regiebehandelaarschap',
    'Psychiatrische spreekuren',
    'Medicatiebeleid',
    'Intercollegiale consultatie',
    'Verslavingszorg'
  ];

  const dagen = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag'];

  const toggleDienst = (dienst) => {
    setFormData(prev => ({
      ...prev,
      diensten: prev.diensten.includes(dienst)
        ? prev.diensten.filter(d => d !== dienst)
        : [...prev.diensten, dienst]
    }));
  };

  const toggleDag = (dag) => {
    setFormData(prev => ({
      ...prev,
      beschikbareDagen: prev.beschikbareDagen.includes(dag)
        ? prev.beschikbareDagen.filter(d => d !== dag)
        : [...prev.beschikbareDagen, dag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepare data including calculator values
    const submitData = {
      ...formData,
      urenPerWeek: calcUren,
      duurSamenwerking: duurLabels[calcDuur],
      totaalUren: totaalUren,
      totaalBedrag: totaalBedrag
    };
    
    // In productie: verstuur naar PHP backend
    // Voor nu: simuleer succesvolle verzending
    setTimeout(() => {
      setStatus({
        type: 'success',
        message: 'Uw offerte aanvraag is succesvol verzonden! U ontvangt binnen 2 werkdagen een reactie.'
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('EUR', '€');
  };

  return (
    <>
      {/* Page Hero */}
      <section className="bg-[#2d5e5e] pt-32 pb-16 text-center text-white">
        <div className="max-w-6xl mx-auto px-5">
          <h1 className="text-3xl font-bold mb-3">Offerte aanvragen</h1>
          <p className="text-lg opacity-90">Vul het formulier in en ontvang direct een offerte. Binnen 2 werkdagen nemen we contact met u op.</p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-5">
          <div className="bg-[#2d5e5e] rounded-2xl p-8 text-white">
            <div className="flex items-center gap-5 mb-8">
              <i className="fas fa-calculator text-4xl text-[#a4cc3a]"></i>
              <div>
                <h2 className="text-xl font-bold">Ureninkoop Calculator</h2>
                <p className="opacity-90 text-sm">Bereken uw ureninkoop voor het komende kwartaal</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm mb-2 opacity-90">Geschatte uren per week</label>
                <input
                  type="number"
                  min="1"
                  max="40"
                  value={calcUren}
                  onChange={(e) => setCalcUren(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-3 bg-white/10 border-2 border-white/30 rounded-lg text-white placeholder-white/50 focus:border-[#a4cc3a] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm mb-2 opacity-90">Aantal maanden</label>
                <select
                  value={calcDuur}
                  onChange={(e) => setCalcDuur(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border-2 border-white/30 rounded-lg text-white focus:border-[#a4cc3a] focus:outline-none cursor-pointer"
                >
                  <option value="3" className="text-gray-900">3 maanden (1 kwartaal)</option>
                  <option value="6" className="text-gray-900">6 maanden (2 kwartalen)</option>
                  <option value="12" className="text-gray-900">12 maanden (1 jaar)</option>
                </select>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-xl p-6 flex flex-wrap justify-between gap-6 mb-4">
              <div className="text-center">
                <span className="block text-sm opacity-80 mb-1">Totaal uren</span>
                <span className="text-2xl font-bold">{totaalUren}</span>
              </div>
              <div className="text-center">
                <span className="block text-sm opacity-80 mb-1">Uurtarief</span>
                <span className="text-2xl font-bold">€ {UURTARIEF},-</span>
              </div>
              <div className="text-center">
                <span className="block text-sm opacity-80 mb-1">Totaal excl. BTW</span>
                <span className="text-3xl font-bold text-[#a4cc3a]">{formatCurrency(totaalBedrag)},-</span>
              </div>
            </div>
            
            <p className="text-sm opacity-80 text-center">
              <i className="fas fa-info-circle mr-2"></i>
              Medische handelingen zijn vrijgesteld van BTW. Niet-gebruikte uren zijn overdraagbaar.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-4xl mx-auto px-5">
          <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
            <form onSubmit={handleSubmit}>
              {/* Section 1: Contact */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-[#2d5e5e] mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 bg-[#2d5e5e] text-white rounded flex items-center justify-center text-sm">
                    <i className="fas fa-user"></i>
                  </span>
                  Uw gegevens
                </h3>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Naam contactpersoon *</label>
                    <input
                      type="text"
                      required
                      value={formData.naam}
                      onChange={(e) => setFormData({...formData, naam: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2d5e5e] focus:outline-none"
                      placeholder="Uw naam"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">E-mailadres *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2d5e5e] focus:outline-none"
                      placeholder="uw@email.nl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">GGZ-instelling / Organisatie *</label>
                    <input
                      type="text"
                      required
                      value={formData.organisatie}
                      onChange={(e) => setFormData({...formData, organisatie: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2d5e5e] focus:outline-none"
                      placeholder="Naam instelling"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Telefoonnummer</label>
                    <input
                      type="tel"
                      value={formData.telefoon}
                      onChange={(e) => setFormData({...formData, telefoon: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2d5e5e] focus:outline-none"
                      placeholder="06-12345678"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Inzet (automatisch ingevuld vanuit calculator) */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-[#2d5e5e] mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 bg-[#2d5e5e] text-white rounded flex items-center justify-center text-sm">
                    <i className="fas fa-calendar-alt"></i>
                  </span>
                  Inzet
                </h3>
                <p className="text-sm text-gray-600 mb-4">Deze gegevens worden automatisch overgenomen van de calculator hierboven.</p>
                <div className="grid md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Geschatte uren per week</label>
                    <input
                      type="text"
                      readOnly
                      value={`${calcUren} uur per week`}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duur samenwerking</label>
                    <input
                      type="text"
                      readOnly
                      value={duurLabels[calcDuur]}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Voorkeursdagen</label>
                  <div className="flex flex-wrap gap-3">
                    {dagen.map((dag) => (
                      <label
                        key={dag}
                        className={`px-6 py-3 rounded-lg border-2 cursor-pointer font-medium transition-all ${
                          formData.beschikbareDagen.includes(dag)
                            ? 'bg-[#2d5e5e] border-[#2d5e5e] text-white'
                            : 'bg-gray-100 border-gray-200 text-gray-700 hover:border-[#2d5e5e]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={formData.beschikbareDagen.includes(dag)}
                          onChange={() => toggleDag(dag)}
                        />
                        {dag}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section 3: Diensten */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-[#2d5e5e] mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 bg-[#2d5e5e] text-white rounded flex items-center justify-center text-sm">
                    <i className="fas fa-stethoscope"></i>
                  </span>
                  Gewenste diensten
                </h3>
                <p className="text-sm text-gray-600 mb-4">Selecteer de diensten waarvoor u ondersteuning zoekt.</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {dienstenOpties.map((dienst) => (
                    <label
                      key={dienst}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.diensten.includes(dienst)
                          ? 'bg-white border-[#2d5e5e]'
                          : 'bg-gray-100 border-transparent hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={formData.diensten.includes(dienst)}
                        onChange={() => toggleDienst(dienst)}
                      />
                      <span className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        formData.diensten.includes(dienst)
                          ? 'bg-[#2d5e5e] border-[#2d5e5e]'
                          : 'border-gray-400'
                      }`}>
                        {formData.diensten.includes(dienst) && (
                          <i className="fas fa-check text-white text-xs"></i>
                        )}
                      </span>
                      <span className="text-sm text-gray-700">{dienst}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Section 4: Toelichting */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-[#2d5e5e] mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 bg-[#2d5e5e] text-white rounded flex items-center justify-center text-sm">
                    <i className="fas fa-comment"></i>
                  </span>
                  Toelichting
                </h3>
                <textarea
                  rows={4}
                  value={formData.toelichting}
                  onChange={(e) => setFormData({...formData, toelichting: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2d5e5e] focus:outline-none resize-y"
                  placeholder="Optionele toelichting op uw aanvraag..."
                />
              </div>

              {/* Summary & Submit */}
              <div>
                <div className="bg-gray-100 rounded-xl p-5 mb-6">
                  <p className="text-gray-700">
                    <strong>Samenvatting:</strong> {calcUren} uur/week × {weeksInPeriod[calcDuur]} weken = <strong>{totaalUren} uren</strong> voor <strong>{formatCurrency(totaalBedrag)},-</strong> (excl. BTW)
                  </p>
                </div>
                
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-600">
                    <input
                      type="checkbox"
                      required
                      checked={formData.privacy}
                      onChange={(e) => setFormData({...formData, privacy: e.target.checked})}
                      className="mt-1 w-5 h-5 accent-[#2d5e5e]"
                    />
                    <span>
                      Ik ga akkoord met de verwerking van mijn gegevens conform de{' '}
                      <a href="/voorwaarden" target="_blank" className="text-[#2d5e5e] underline">algemene voorwaarden</a> *
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary justify-center text-lg py-4 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Verzenden...' : 'Offerte aanvragen'}
                  <i className="fas fa-paper-plane ml-2"></i>
                </button>

                {status.message && (
                  <div className={`mt-5 p-4 rounded-lg ${
                    status.type === 'success' 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {status.message}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
